import routerConfig from '@/config/router';
import useThemeToken from '@/hooks/useTheme';
import { useLocales } from '@/Locales';
import { useCacheTab } from '@/store/cacheTab';
import { globalFontSizeKeyAtom } from '@/store/theme';
import { CloseOutlined } from '@ant-design/icons';
import { Dropdown, Menu, Tabs } from '@sinohealth/butterfly-ui-components';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useAliveController } from 'react-activation';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import styles from './index.less';

interface CacheBarComponentProps {
  onChange: (height: number) => void;
}

const CacheBar: React.FC<CacheBarComponentProps> = ({ onChange }) => {
  const themeToken = useThemeToken();
  const navigate = useNavigate();
  const location = useLocation();
  const cacheBarRef = useRef<HTMLDivElement>(null);
  const { tabs, addTab, closeTab, closeOtherTabs, closeRightTabs, closeLeftTabs } = useCacheTab();
  const { drop, getCachingNodes, refresh } = useAliveController();
  const globalFontSizeKey = useRecoilValue(globalFontSizeKeyAtom);
  const { t } = useLocales();

  // 用于管理标签页数据和当前激活标签
  // const [tabs, setTabs] = useState<any>([]);
  const [activeKey, setActiveKey] = useState('');

  const lastNodesRef = React.useRef<any[]>([]);

  /**
   * 从路由配置中提取所有 path-name 键值对，生成 Map
   * @param routes 路由配置数组
   * @returns Map<path, name>
   */
  const extractPathNameMap = (routes: any[]): Map<string, string> => {
    const result = new Map<string, string>();

    function traverse(list: any[]) {
      list.forEach((item) => {
        if (item.path && item.name && item.cached) {
          result.set(item.path, item.name);
        }
        if (Array.isArray(item.children)) {
          traverse(item.children);
        }
      });
    }

    traverse(routes);
    return result;
  };

  useEffect(() => {
    setActiveKey(location.pathname);
  }, [location.pathname]);

  // 用useMemo缓存menuMap，只有routerConfig变化时才重新生成
  const menuMap = React.useMemo(
    () => extractPathNameMap(routerConfig[0].children || []),
    [routerConfig],
  );
  const nodes = getCachingNodes();

  // 辅助函数：只比较节点的key或name，避免循环引用
  const getNodeKeys = (arr: any[]) => (arr || []).map((item) => item.id || item.key).join(',');

  useEffect(() => {
    if (getNodeKeys(lastNodesRef.current) === getNodeKeys(nodes)) return;
    lastNodesRef.current = nodes;
    nodes?.forEach((node: any) => {
      const hash = window.location.hash?.replace('#', '');
      const splitHash = hash?.split('?');
      addTab({
        key: node.id,
        label: menuMap.get(node.id) || '',
        path: node.id,
        params: splitHash[0] === node.id ? splitHash?.[1] : '',
      });
    });
    // const cacheTabs =
    //   nodes?.map((node: any) => ({
    //     key: node.id,
    //     label: (
    //       <div>
    //         {menuMap.get(node.id)} <CloseOutlined />
    //       </div>
    //     ),
    //   })) || [];
    // console.log({ cacheTabs, nodes });
    // if (getNodeKeys(tabs) !== getNodeKeys(cacheTabs)) {
    //   addTab(cacheTabs);
    // }
  }, [nodes, menuMap]);

  // 获取缓存栏高度
  useEffect(() => {
    const updateHeight = () => {
      if (cacheBarRef.current) {
        const height = cacheBarRef.current.offsetHeight;
        onChange?.(height);
      }
    };

    updateHeight();

    // 使用 ResizeObserver 监听高度变化
    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    if (cacheBarRef.current) {
      resizeObserver.observe(cacheBarRef.current);
    }
  }, [tabs, globalFontSizeKey, onChange]);

  // 切换激活标签
  const handleTabClick = (key: string) => {
    const tab = tabs.find((ta: any) => ta.key === key);
    // 如果是详情页，先刷新缓存
    navigate(tab?.params ? `${key}?${tab?.params}` : key);
    // refresh(key);
    setActiveKey(key);
  };

  // 关闭标签
  const handleClose = (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    // 如果只有一个标签，不允许关闭
    if (tabs.length <= 1) {
      return;
    }

    // 如果关闭的是当前选中的标签，需要先选择下一个或前一个标签
    if (key === activeKey) {
      const currentIndex = tabs.findIndex((tab: any) => tab.key === activeKey);
      const nextTab = tabs[currentIndex + 1] || tabs[currentIndex - 1];

      if (nextTab) {
        navigate(nextTab.params ? `${nextTab.key}?${nextTab.params}` : nextTab.key);
        setActiveKey(nextTab.key);
      }
    }

    closeTab(key);
    drop(key);
  };

  return (
    <div
      ref={cacheBarRef}
      id="cacheBar"
      className={classNames(styles.cacheBar)}
      style={{ backgroundColor: themeToken.colorBgContainer }}
    >
      <Tabs
        activeKey={activeKey}
        tabBarGutter={0}
        items={tabs.map((tab) => ({
          key: tab.key,
          label:
            activeKey === tab.key ? (
              <Dropdown
                dropdownRender={() => (
                  <Menu>
                    <Menu.Item
                      key="refresh"
                      onClick={() => {
                        refresh(tab.key);
                      }}
                    >
                      {t('刷新标签')}
                    </Menu.Item>
                    <Menu.Item
                      key="close"
                      onClick={() => {
                        if (tabs.length > 1) {
                          const currentIndex = tabs.findIndex((tb: any) => tb.key === activeKey);
                          const nextTab = tabs[currentIndex + 1] || tabs[currentIndex - 1];

                          closeTab(activeKey);
                          drop(tab.key);

                          // 切换到下一个或前一个标签
                          if (nextTab) {
                            navigate(
                              nextTab.params ? `${nextTab.key}?${nextTab.params}` : nextTab.key,
                            );
                            setActiveKey(nextTab.key);
                          }
                        }
                      }}
                    >
                      {t('关闭标签')}
                    </Menu.Item>
                    <Menu.Item
                      key="closeOthers"
                      onClick={() => {
                        if (tabs.length > 1) {
                          closeOtherTabs(tab.key);
                          tabs.forEach((tb) => {
                            if (tb.key !== tab.key) {
                              drop(tb.key);
                            }
                          });
                        }
                      }}
                    >
                      {t('关闭其他标签')}
                    </Menu.Item>
                    <Menu.Item
                      key="closeRight"
                      onClick={() => {
                        const index = tabs.findIndex((tb) => tb.key === tab.key);
                        if (index < tabs.length - 1) {
                          closeRightTabs(tab.key);
                          tabs.slice(index + 1).forEach((tb) => {
                            drop(tb.key);
                          });
                        }
                      }}
                    >
                      {t('关闭右侧标签')}
                    </Menu.Item>
                    <Menu.Item
                      key="closeLeft"
                      onClick={() => {
                        const index = tabs.findIndex((tb) => tb.key === tab.key);
                        if (index > 0) {
                          closeLeftTabs(tab.key);
                          tabs.slice(0, index).forEach((tb) => {
                            drop(tb.key);
                          });
                        }
                      }}
                    >
                      {t('关闭左侧标签')}
                    </Menu.Item>
                  </Menu>
                )}
                trigger={['contextMenu']}
              >
                <div className={styles.tabLabel}>
                  {t(tab.label)}
                  {tabs.length > 1 && <CloseOutlined onClick={(e) => handleClose(e, tab.key)} />}
                </div>
              </Dropdown>
            ) : (
              <div className={styles.tabLabel}>
                {t(tab.label)}
                {tabs.length > 1 && <CloseOutlined onClick={(e) => handleClose(e, tab.key)} />}
              </div>
            ),
        }))}
        onChange={handleTabClick}
      />
    </div>
  );
};

export default CacheBar;
